"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ShieldCheck, ThermometerSnowflake, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface TrailerUnit {
  id: string;
  title: string;
  subtitle: string;
  image: string;
  srcset?: string;
  sizes?: string;
  alt: string;
  feature1: string;
  feature2: string;
  feature3: string;
  feature3b: string;
  feature4: string;
}
const trailers: TrailerUnit[] = [{
  id: "cooler-trailer",
  title: "6 × 8 Refrigerated & Freezer Trailer",
  subtitle: "Verified specifications",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/6x8-FF-trailer.png?v=q5a34b2kb0s",
  srcset: "https://fridgefleet.com/wp-content/uploads/2026/09/6x8-FF-trailer.png?v=y8l1hfe5p4-wvc-srcset",
  sizes: "https://fridgefleet.com/wp-content/uploads/2026/09/6x8-FF-trailer.png?v=4ycsc1r7zry-wvc-sizes",
  alt: "Fridge Fleet refrigerated trailer parked at a restaurant receiving dock.",
  feature1: "Operates using one standard 115-volt, 15-amp electrical circuit.",
  feature2: "Maintains temperatures between −10°F and 40°F when properly connected and operated.",
  feature3: "Optional Honda 3000 generator available.",
  feature3b: "Optional interior shelving available; pricing confirmed with quote.",
  feature4: "Exterior: 144″ L × 92″ W × 92″ H. Interior: 89.5″ L × 65″ W × 62″ H"
}, {
  id: "freezer-trailer",
  title: "7 × 12 Refrigerated & Freezer Trailer",
  subtitle: "Verified specifications",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_47_20-PM-1.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_47_20-PM-1.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796053/img/ChatGPT-Image-Sep-12-2026-06_47_20-PM-1.png-wvc-sizes",
  alt: "Fridge Fleet freezer trailer parked at a warehouse loading dock.",
  feature1: "Operates using one standard 115-volt, 15-amp electrical circuit.",
  feature2: "Maintains temperatures between −10°F and 40°F when properly connected and operated.",
  feature3: "Optional Honda 3000 generator available.",
  feature3b: "Optional interior shelving available; pricing confirmed with quote.",
  feature4: "Exterior: 192″ L × 102″ W × 98″ H. Interior: 144″ L × 74″ W × 72″ H"
}, {
  id: "multi-temp-trailer",
  title: "7 × 16 Refrigerated & Freezer Trailer",
  subtitle: "Verified specifications",
  image: "https://fridgefleet.com/wp-content/uploads/2026/09/7x16-FF-trailer.png?v=m9xxdxwshsf",
  srcset: "https://fridgefleet.com/wp-content/uploads/2026/09/7x16-FF-trailer.png?v=m4wt2zg41x-wvc-srcset",
  sizes: "https://fridgefleet.com/wp-content/uploads/2026/09/7x16-FF-trailer.png?v=ez1b6cpwmtk-wvc-sizes",
  alt: "Fridge Fleet mobile refrigeration trailer parked backstage at an outdoor event.",
  feature1: "Operates using one standard 115-volt, 15-amp electrical circuit.",
  feature2: "Maintains temperatures between −10°F and 40°F when properly connected and operated.",
  feature3: "Optional Honda 3000 generator available.",
  feature3b: "Optional interior shelving available; pricing confirmed with quote.",
  feature4: "Exterior: 240″ L × 102″ W × 92″ H. Interior: 192″ L × 74″ W × 72″ H"
}];
export default function Lineup() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="trailer-lineup" data-nav="light" className="bg-background text-foreground py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 border border-accent/40 text-accent-foreground text-xs font-bold tracking-widest uppercase mb-4">
            <ThermometerSnowflake className="size-3.5" />
            <span>EQUIPMENT INVENTORY</span>
          </div>

          <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground mb-4">Mobile Refrigerated &amp; Freezer Trailer Rentals</h1>

          <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed">Explore Fridge Fleet's 6×8, 7×12, and 7×16 mobile refrigerated and freezer trailers with verified specifications, plus a 53-foot reefer trailer for larger temporary refrigerated storage needs. Specifications and suitability for the 53-foot reefer are confirmed individually.</p>
        </div>

        {/* 3-Column Trailer Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {trailers.map((unit, index) => <motion.div key={unit.id} variants={cardVariants} data-index={index}>
              <Card className="h-full flex flex-col justify-between overflow-hidden rounded-lg border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200 hover:border-primary/40">
                <div>
                  {/* Trailer Image Container */}
                  <div className="relative aspect-16/10 w-full overflow-hidden bg-muted border-b border-border">
                    <img className="w-full h-full object-cover" src={unit.image} data-wvc-srcset={unit.srcset} data-wvc-sizes={unit.sizes} alt={unit.alt} loading="lazy" />
                    <div className="absolute top-3 right-3 left-3 flex justify-end">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-semibold shadow-xs text-right whitespace-normal">
                        Verified specifications
                      </span>
                    </div>
                  </div>

                  <CardHeader className="pt-6 pb-2">
                    <div className="mb-2">
                      <Badge variant="outline" className="bg-muted text-muted-foreground border-border text-[11px] font-medium tracking-normal">
                        verified specifications
                      </Badge>
                    </div>

                    <CardTitle className="font-default text-xl lg:text-2xl font-bold text-card-foreground">
                      {unit.title}
                    </CardTitle>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {unit.subtitle}
                    </p>
                  </CardHeader>

                  <CardContent className="pt-3 pb-6">
                    <div className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                      Specifications:
                    </div>
                    <ul className="space-y-2.5 text-sm text-card-foreground">
                      <li className="flex items-start gap-2.5">
                        <Check className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{unit.feature1}</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <Check className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{unit.feature2}</span>
                      </li>
                      <li className="pl-6.5 text-xs text-muted-foreground leading-relaxed">
                        Temperature performance may vary based on ambient conditions, loading, door usage, electrical supply, and operating conditions.
                      </li>
                      <li className="flex items-start gap-2.5">
                        <Check className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{unit.feature3}</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <Check className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{unit.feature3b}</span>
                      </li>
                      <li className="flex items-start gap-2.5">
                        <Check className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{unit.feature4}</span>
                      </li>
                    </ul>
                  </CardContent>
                </div>

                <CardFooter className="pt-2 pb-6 border-t border-border/60">
                  <Button asChild variant="secondary" className="w-full rounded-md font-semibold text-sm h-11 transition-[background-color,color] hover:bg-secondary/90">
                    <Link to="/ContactRequestAQuotePage">
                      Request a Quote
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Dispatch Quick Help Strip */}
        <div className="mt-12 rounded-lg border border-border bg-card p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-md bg-accent/20 border border-accent/30 flex items-center justify-center text-accent-foreground shrink-0">
              <ShieldCheck className="size-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Verified specifications for the 6×8, 7×12, and 7×16 trailers are provided below. Specifications and delivery requirements for the 53-foot reefer are confirmed individually.</p>
              <p className="text-xs text-muted-foreground">Operating across Southern California and our listed Las Vegas-area communities.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Button asChild variant="default" className="w-full sm:w-auto bg-primary text-primary-foreground font-semibold text-sm px-6 h-10">
              <Link to="/ContactRequestAQuotePage">
                <Zap className="size-4 mr-1" />
                Request a Quote
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}